"use client";

import { useEffect, useRef, useState } from "react";

/* The house songs, in the header on every store page (the same ten the Foundry plays).
 * Recorded on our own audio engine; nothing loads until the visitor presses play, and the layout stays
 * mounted between pages, so the music keeps going while they browse. */
const SONGS = [
  { name: "Say It Once", mood: "Hard rap, male", src: "/foundry/audio/say-it-once.mp3" },
  { name: "Pick It, Own It", mood: "Boom bap rap, male", src: "/foundry/audio/pick-it-own-it.mp3" },
  { name: "Main Street Dream", mood: "Upbeat country, male", src: "/foundry/audio/main-street-dream.mp3" },
  { name: "Back Road Sign", mood: "Country ballad, male", src: "/foundry/audio/back-road-sign.mp3" },
  { name: "Pretty Site", mood: "Country, female", src: "/foundry/audio/pretty-site.mp3" },
  { name: "Build It Loud", mood: "Hard rock, male", src: "/foundry/audio/build-it-loud.mp3" },
  { name: "Let It Rise", mood: "Anthem rock, male", src: "/foundry/audio/let-it-rise.mp3" },
  { name: "Own the Night", mood: "Rock, female", src: "/foundry/audio/own-the-night.mp3" },
  { name: "Click, Save", mood: "Pop, female", src: "/foundry/audio/click-save.mp3" },
  { name: "Golden Hour", mood: "Pop, male", src: "/foundry/audio/golden-hour.mp3" },
] as const;

export function MusicMenu() {
  const [open, setOpen] = useState(false);
  const [song, setSong] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [vol, setVol] = useState(50);
  const wrap = useRef<HTMLDivElement>(null);
  const cur = useRef<HTMLAudioElement | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const volRef = useRef(0.5);

  const fade = (a: HTMLAudioElement, to: number, ms: number) => {
    const from = a.volume;
    const t0 = performance.now();
    const step = () => {
      const k = Math.min(1, (performance.now() - t0) / ms);
      a.volume = Math.max(0, Math.min(1, from + (to - from) * k));
      if (k < 1) requestAnimationFrame(step);
      else if (to === 0) a.pause();
    };
    step();
  };

  const stop = () => {
    if (timer.current) clearInterval(timer.current);
    if (cur.current) fade(cur.current, 0, 500);
    cur.current = null;
    setPlaying(false);
  };

  const start = (i: number) => {
    if (timer.current) clearInterval(timer.current);
    if (cur.current) fade(cur.current, 0, 500);
    const a = new Audio(SONGS[i]!.src);
    a.volume = 0;
    a.play().catch(() => setPlaying(false));
    fade(a, volRef.current, 1500);
    cur.current = a;
    setSong(i);
    setPlaying(true);
    timer.current = setInterval(() => {
      const c = cur.current;
      if (!c || !c.duration || c.duration - c.currentTime > 3.2 || (c as HTMLAudioElement & { _n?: boolean })._n) return;
      (c as HTMLAudioElement & { _n?: boolean })._n = true;
      const n = new Audio(SONGS[i]!.src);
      n.volume = 0;
      n.play().catch(() => {});
      fade(n, volRef.current, 3000);
      fade(c, 0, 3000);
      cur.current = n;
    }, 400);
  };

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (open && wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    addEventListener("pointerdown", onDown);
    addEventListener("keydown", onKey);
    return () => {
      removeEventListener("pointerdown", onDown);
      removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => () => {
    if (timer.current) clearInterval(timer.current);
    cur.current?.pause();
  }, []);

  return (
    <div ref={wrap} className="relative">
      <button
        type="button"
        data-interactive
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls="store-music"
        className="inline-flex min-h-[44px] items-center gap-2 rounded-full border px-4 text-[14px] font-bold"
        style={{ borderColor: "rgba(217,163,90,0.45)", background: "rgba(20,16,12,0.7)", color: "var(--color-fg)" }}
      >
        <span aria-hidden>♪</span>
        <span className="hidden max-w-[11ch] truncate sm:inline">{playing ? SONGS[song]!.name : "Music"}</span>
        <span className="sr-only">{playing ? `Now playing ${SONGS[song]!.name}. Open music menu` : "Open music menu"}</span>
      </button>
      {open && (
        <div
          id="store-music"
          role="group"
          aria-label="Music player"
          className="absolute right-0 top-[calc(100%+10px)] z-[70] w-[min(330px,88vw)] rounded-[18px] border p-3.5"
          style={{ background: "rgba(20,16,12,0.97)", borderColor: "#7a4a22", boxShadow: "0 24px 60px -12px #000c", backdropFilter: "blur(14px)" }}
        >
          <p className="mb-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: "var(--color-accent)" }}>
            Our songs, made on our own audio engine
          </p>
          <ul className="mb-3 grid max-h-[52vh] gap-1.5 overflow-y-auto">
            {SONGS.map((s, i) => (
              <li key={s.name}>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={playing && song === i}
                  onClick={() => start(i)}
                  className="grid min-h-[44px] w-full gap-0.5 rounded-xl border px-3 py-2.5 text-left"
                  style={{
                    borderColor: playing && song === i ? "#f0c98a" : "rgba(255,255,255,0.12)",
                    background: playing && song === i ? "linear-gradient(135deg,#3a2310,#6b4421)" : "#1c1610",
                    color: "#fff6e6",
                  }}
                >
                  <b className="text-[15px]">{playing && song === i ? "▶  " : ""}{s.name}</b>
                  <span className="text-[12.5px]" style={{ color: "#c8b79f" }}>{s.mood}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => (playing ? stop() : start(song))}
              aria-pressed={playing}
              className="min-h-[44px] rounded-full px-5 text-[14px] font-extrabold"
              style={{ background: "linear-gradient(135deg,#7a4a22,#c98b4a 24%,#f6dcab 42%,#b87333 62%,#e6b36a)", color: "#1a0f06" }}
            >
              {playing ? "Pause" : "Play"}
            </button>
            <label className="flex items-center gap-2 text-[12.5px] font-semibold" style={{ color: "#c8b79f" }}>
              Volume
              <input
                type="range"
                min={0}
                max={100}
                value={vol}
                aria-label="Music volume"
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setVol(v);
                  volRef.current = v / 100;
                  if (cur.current) cur.current.volume = v / 100;
                }}
                className="w-[110px]"
                style={{ accentColor: "#d9a35a" }}
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
